import type { LTLItem } from 'pages/QuoteView/Quoting/RequestQuickQuote/types';
import { Undef } from 'types/UtilityTypes';

import { normalizeHandlingUnitsForPacking } from './ltlMetrics';
import {
  SIDELOAD_MAX_SPLIT_DEPTH,
  allocateDerivedItemId,
  splitSideLoadLinePiecesByHuShares,
  splitSideLoadLineWeightByHuShares,
} from './sideLoading';

// Shipment-level side-load uses a one-way internal flag: when enabled, force
// `canSideLoad=true` on all rows. Disable/reset is handled by caller policy.
export function enableShipmentSideLoadFlagOnAllItems(
  items: LTLItem[],
  options: {
    sideLoadEnabled: boolean;
  }
): LTLItem[] {
  if (!options.sideLoadEnabled) {
    return items.map((item) => ({ ...item }));
  }

  return items.map((item) => ({
    ...item,
    canSideLoad: true,
  }));
}

const numericFieldsToMatch = [
  'length',
  'width',
  'height',
  'linearLength',
  'minTemp',
  'maxTemp',
] as const;

const stringFieldsToMatch = [
  'nmfcCode',
  'commodityExternalTmsId',
  'handlingUnitType',
  'dimensionUnit',
  'weightUnit',
] as const;

const booleanFieldsToMatch = ['canSideLoad', 'nonStackable'] as const;

const hazmatFieldsToMatch = [
  'unClass',
  'packingGroup',
  'hazmatClass',
  'properShippingName',
  'hazmatContactNumber',
  'hazmatContactName',
  'subsidiaryClass',
  'erapReference',
] as const;

function hazmatCompatible(a: LTLItem, b: LTLItem): boolean {
  if (Boolean(a.hazmat) !== Boolean(b.hazmat)) {
    return false;
  }
  if (!a.hazmat && !b.hazmat) {
    return true;
  }

  for (const field of hazmatFieldsToMatch) {
    if (a[field] !== b[field]) {
      return false;
    }
  }
  return true;
}

// Whether two commodity rows may be merged after a side-load apply.
// Merge is allowed only for rows that explicitly share the same parent lineage.
export function canMergeSideLoadFragments(a: LTLItem, b: LTLItem): boolean {
  for (const field of numericFieldsToMatch) {
    if (a[field] !== b[field]) {
      return false;
    }
  }

  if (a.description?.trim() !== b.description?.trim()) {
    return false;
  }

  if (a.packageType !== b.packageType) {
    return false;
  }

  if (a.parentItemId == null || b.parentItemId == null) {
    return false;
  }

  if (a.parentItemId !== b.parentItemId) {
    return false;
  }

  for (const field of stringFieldsToMatch) {
    if (a[field] !== b[field]) {
      return false;
    }
  }

  for (const field of booleanFieldsToMatch) {
    if (a[field] !== b[field]) {
      return false;
    }
  }

  if (!hazmatCompatible(a, b)) {
    return false;
  }

  const firstHandlingUnits = normalizeHandlingUnitsForPacking(a.handlingUnits);
  const secondHandlingUnits = normalizeHandlingUnitsForPacking(b.handlingUnits);
  if (firstHandlingUnits <= 0 || secondHandlingUnits <= 0) {
    return false;
  }
  return true;
}

// Combines line-total weight and pieces by addition. Partial splits already used
// HU-share splitters so merged totals remain consistent.
function mergeSideLoadFragments(primary: LTLItem, incoming: LTLItem): LTLItem {
  const primaryHandlingUnits = normalizeHandlingUnitsForPacking(
    primary.handlingUnits
  );
  const incomingHandlingUnits = normalizeHandlingUnitsForPacking(
    incoming.handlingUnits
  );
  const mergedHandlingUnits = primaryHandlingUnits + incomingHandlingUnits;

  let mergedWeight: Undef<number>;
  if (primary.weight != null && incoming.weight != null) {
    mergedWeight = primary.weight + incoming.weight;
  } else {
    mergedWeight = primary.weight ?? incoming.weight;
  }

  let mergedPieces: Undef<number>;
  if (primary.pieces != null && incoming.pieces != null) {
    mergedPieces = primary.pieces + incoming.pieces;
  } else {
    mergedPieces = primary.pieces ?? incoming.pieces;
  }

  return {
    ...primary,
    itemId: primary.itemId,
    handlingUnits: mergedHandlingUnits,
    weight: mergedWeight,
    pieces: mergedPieces,
  };
}

function tryMergeOrPush(acc: LTLItem[], candidate: LTLItem): void {
  const mergeIdx = acc.findIndex((row) =>
    canMergeSideLoadFragments(row, candidate)
  );
  if (mergeIdx >= 0) {
    acc[mergeIdx] = mergeSideLoadFragments(acc[mergeIdx], candidate);
    return;
  }

  acc.push(candidate);
}

function pushWithMergePolicy(
  acc: LTLItem[],
  candidate: LTLItem,
  allowSplitMerge: boolean
): void {
  if (!allowSplitMerge) {
    acc.push(candidate);
    return;
  }

  tryMergeOrPush(acc, candidate);
}

function clearFreightClassSelection(item: LTLItem): LTLItem {
  return {
    ...item,
    freightClass: '',
    estimatedFreightClass: '',
    usingEstimatedFreightClass: false,
    showEstimatedFreightClassMessage: false,
  };
}

// Applies side-load rotations and partial splits, merging into an existing row when compatible.
export function applySideLoadSuggestionsWithMerge(
  items: LTLItem[],
  suggestionsByItemId: Map<string, number>,
  options?: {
    allowSplitMerge?: boolean;
  }
): LTLItem[] {
  const allowSplitMerge = options?.allowSplitMerge ?? true;
  const acc: LTLItem[] = [];

  for (const item of items) {
    const handlingUnitCount = normalizeHandlingUnitsForPacking(
      item.handlingUnits
    );
    const rotatedUnitsRaw = suggestionsByItemId.get(item.itemId) ?? 0;
    const rotatedUnits = Math.max(
      0,
      Math.min(handlingUnitCount, Math.floor(rotatedUnitsRaw))
    );
    const splitDepth = item.splitDepth ?? 0;
    const canSplitThisRow =
      allowSplitMerge && splitDepth < SIDELOAD_MAX_SPLIT_DEPTH;
    const effectiveRotatedUnits =
      canSplitThisRow ||
      rotatedUnits === 0 ||
      rotatedUnits === handlingUnitCount
        ? rotatedUnits
        : 0;

    if (handlingUnitCount <= 0 || effectiveRotatedUnits === 0) {
      // Preserve untouched base rows exactly; avoid merging two independent lines
      // when no side-load suggestion applies to this item.
      // Pre-existing derived rows (with parentItemId) can still merge with a
      // newly generated compatible fragment from another suggested item.
      if (item.parentItemId == null) {
        acc.push({ ...item });
      } else {
        pushWithMergePolicy(acc, { ...item }, allowSplitMerge);
      }
      continue;
    }

    if (effectiveRotatedUnits === handlingUnitCount) {
      pushWithMergePolicy(
        acc,
        clearFreightClassSelection({
          ...item,
          parentItemId: item.parentItemId ?? item.itemId,
          length: item.width,
          width: item.length,
          height: item.height,
        }),
        allowSplitMerge
      );
      continue;
    }

    const normalHandlingUnits = handlingUnitCount - effectiveRotatedUnits;
    const rotatedHandlingUnits = effectiveRotatedUnits;
    const { normal: normalWeight, rotated: rotatedWeight } =
      splitSideLoadLineWeightByHuShares(
        item.weight,
        normalHandlingUnits,
        rotatedHandlingUnits
      );
    const { normal: normalPieces, rotated: rotatedPieces } =
      splitSideLoadLinePiecesByHuShares(
        item.pieces,
        normalHandlingUnits,
        rotatedHandlingUnits
      );

    const parentItemId = item.itemId;
    const rotatedItemId = allocateDerivedItemId();

    const normalRow: LTLItem = clearFreightClassSelection({
      ...item,
      // Normal half keeps original orientation, so clear split lineage.
      // This avoids incorrect "Split from ..." tagging and unintended merges
      // with other derived rows that share the same ancestor.
      parentItemId: undefined,
      handlingUnits: normalHandlingUnits,
      weight: normalWeight,
      pieces: normalPieces,
      splitDepth: splitDepth + 1,
    });

    const rotatedRow: LTLItem = clearFreightClassSelection({
      ...item,
      itemId: rotatedItemId,
      parentItemId,
      length: item.width,
      width: item.length,
      height: item.height,
      handlingUnits: rotatedHandlingUnits,
      weight: rotatedWeight,
      pieces: rotatedPieces,
      splitDepth: splitDepth + 1,
    });

    acc.push(normalRow);
    pushWithMergePolicy(acc, rotatedRow, allowSplitMerge);
  }

  return acc;
}
